import React from "react";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import useScaleWeight from "../../common/lib/hooks/useScaleWeight";
import KaAlert from "../../common/lib/KaAlert";
import Container from "../../common/Container";
import Footer from "../../common/Footer";
import FooterButton from "../../common/FooterButton";
import useAcceptTareWeight from "../../requests/useAcceptTareWeight";
import { useLingui } from "@lingui/react";
import { msg, Trans } from "@lingui/macro";
import { driverFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import Row from "../../common/Row";
import { unitsToDisplay } from "../../common/units/unitsToDisplay";
import { convertWeight } from "../../common/units/unitConversion";
import { Units } from "../../common/units/units";
import ScaleReconnection from "../../common/components/ScaleReconnection";

export interface TareWeightProps {
  siteId: string;
  scaleId: string;
  soId: string;
  requestedAmount: number;
  officePhoneNumber?: string;
  tareAccepted: (bayName?: string) => void;
  devToolItems?: React.ReactNode;
}

const TareWeight = (props: TareWeightProps) => {
  const { _ } = useLingui();
  const displayMessage = useDisplayMessage();

  const { weight, isReconnecting, showEscalationMessage } = useScaleWeight({
    siteId: props.siteId,
    scaleId: props.scaleId
  });

  const isWeightInvalid = weight.value <= 0;

  const acceptTare = useAcceptTareWeight(props.soId, {
    onSuccess: ({ bayName }) => props.tareAccepted(bayName),
    onError: err => displayMessage.fail(_(driverFacingErrorMessage(err))),
  });

  return <Container
    title={_(msg`Verify Tare Weight`)}
    devToolItems={props.devToolItems}>
    <div className="m-auto w-75">
      {
        isReconnecting
          ? <ScaleReconnection 
              showEscalationMessage={showEscalationMessage}
              officePhoneNumber={props.officePhoneNumber} />
          : <Row>
            <KaAlert
              displayMessage={displayMessage.message}
              onClose={displayMessage.clear} />
            <div className="col-7 mt-5">
              <label className="h5" htmlFor='tareWeight'><Trans>Tare Weight</Trans></label>
              <div className="input-group input-group-sm">
                <input
                  id='tareWeight'
                  type='text'
                  className='form-control form-control-sm p-2 text-end'
                  value={weight.value}
                  readOnly />
                <div className="input-group-append input-group-text">
                  {_(unitsToDisplay(weight.unit))}
                </div>
              </div>
            </div>
            <div className="col-5 mt-5 text-end p-1">
              <div>
                <h5><Trans>Projected Gross Weight</Trans></h5>
              </div>
              <div>
                <label>
                  {_(msg`${props.requestedAmount + convertWeight(weight, Units.lb)} lb`)}
                </label>
              </div>
            </div>
          </Row>
      }
    </div>
    <Footer nextButton={
      <FooterButton
        spinning={acceptTare.isLoading}
        onClick={() => acceptTare.request()}
        disabled={isReconnecting || isWeightInvalid || acceptTare.isLoading}>
        <Trans>Accept Weight</Trans>
      </FooterButton>
    } />
  </Container>
}

export default TareWeight;
